import type { ArenaMap, PlayerCount, PlayerSlot, WorldObjectDefinition, WorldObjectKind } from "./types";

export type MdmObjectType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface MdmObject {
  readonly type: MdmObjectType;
  readonly x: number;
  readonly y: number;
  readonly targetX?: number;
  readonly targetY?: number;
}

export interface MdmMap {
  readonly version: "1.1" | "1.2";
  readonly name: string;
  readonly players: Readonly<Partial<Record<PlayerSlot, { readonly x: number; readonly y: number }>>>;
  readonly objects: readonly MdmObject[];
  readonly fixedAbilityIds: readonly number[] | null;
}

export interface MdmValidationIssue {
  readonly path: string;
  readonly message: string;
}

type IniDocument = Record<string, Record<string, string>>;

const parseIni = (source: string): IniDocument => {
  const document: IniDocument = {};
  let section = "";
  for (const rawLine of source.replace(/^\uFEFF/, "").split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line === "" || line.startsWith(";") || line.startsWith("#")) continue;
    const sectionMatch = line.match(/^\[([^\]]+)]$/);
    if (sectionMatch) {
      section = sectionMatch[1]!.trim().toUpperCase();
      document[section] ??= {};
      continue;
    }
    const separator = line.indexOf("=");
    if (separator < 0 || section === "") continue;
    const key = line.slice(0, separator).trim().toLowerCase();
    const currentSection = document[section] ?? (document[section] = {});
    currentSection[key] = line.slice(separator + 1).trim();
  }
  return document;
};

const finiteNumber = (value: string | undefined, fallback = 0): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const isObjectType = (value: number): value is MdmObjectType => Number.isInteger(value) && value >= 1 && value <= 12;

export const parseMdm = (source: string): MdmMap => {
  const ini = parseIni(source);
  const main = ini.MAIN ?? {};
  const version = main.mapver === "1.2" ? "1.2" : "1.1";
  const declaredCount = Math.max(0, Math.min(2048, Math.floor(finiteNumber(main.count))));
  const players: Partial<Record<PlayerSlot, { x: number; y: number }>> = {};
  for (let slot = 1; slot <= 4; slot += 1) {
    const x = ini.PLAYERS?.[`p${slot}x`];
    const y = ini.PLAYERS?.[`p${slot}y`];
    if (x !== undefined && y !== undefined) players[slot as PlayerSlot] = { x: finiteNumber(x), y: finiteNumber(y) };
  }
  const objects: MdmObject[] = [];
  for (let index = 1; index <= declaredCount; index += 1) {
    const section = ini[String(index)] ?? {};
    const type = Math.floor(finiteNumber(section.type));
    if (!isObjectType(type)) continue;
    objects.push({
      type,
      x: finiteNumber(section.x),
      y: finiteNumber(section.y),
      ...(type === 5 ? { targetX: finiteNumber(section.telex), targetY: finiteNumber(section.teley) } : {})
    });
  }
  const abilityMode = ini.ABILITIES?.mode?.toLowerCase();
  const fixedAbilityIds = abilityMode === "fixed"
    ? [...new Set((ini.ABILITIES?.ids ?? "").split(",").map((id) => Number(id.trim())).filter((id) => Number.isInteger(id) && id >= 1 && id <= 48))]
    : null;
  return {
    version,
    name: main.mapname?.trim() || "Map",
    players,
    objects,
    fixedAbilityIds: fixedAbilityIds?.length ? fixedAbilityIds : null
  };
};

export const validateMdm = (map: MdmMap): readonly MdmValidationIssue[] => {
  const issues: MdmValidationIssue[] = [];
  if (map.name.trim().length === 0 || map.name.length > 48) issues.push({ path: "name", message: "Map name must contain 1 to 48 characters." });
  if (map.objects.length > 2048) issues.push({ path: "objects", message: "Map contains more than 2048 objects." });
  const playerSlots = Object.keys(map.players).map(Number).sort();
  if (playerSlots.length < 2 || playerSlots.length > 4) issues.push({ path: "players", message: "Map must contain 2 to 4 player starts." });
  const checkPoint = (path: string, x: number, y: number) => {
    if (!Number.isFinite(x) || x < -64 || x > 864) issues.push({ path: `${path}.x`, message: "X coordinate is outside the allowed range." });
    if (!Number.isFinite(y) || y < -64 || y > 664) issues.push({ path: `${path}.y`, message: "Y coordinate is outside the allowed range." });
  };
  for (const [slot, point] of Object.entries(map.players)) if (point) checkPoint(`players.${slot}`, point.x, point.y);
  map.objects.forEach((object, index) => {
    checkPoint(`objects.${index}`, object.x, object.y);
    if (object.type === 5) checkPoint(`objects.${index}.target`, object.targetX ?? 0, object.targetY ?? 0);
  });
  if (map.fixedAbilityIds?.some((id) => !Number.isInteger(id) || id < 1 || id > 48)) issues.push({ path: "fixedAbilityIds", message: "Ability IDs must be between 1 and 48." });
  return issues;
};

export const serializeMdm = (map: MdmMap): string => {
  const lines = [
    "[MAIN]",
    `mapname=${map.name}`,
    "mapver=1.2",
    `count=${map.objects.length}`,
    "",
    "[PLAYERS]"
  ];
  for (const [slot, position] of Object.entries(map.players).sort(([left], [right]) => Number(left) - Number(right))) {
    if (!position) continue;
    lines.push(`p${slot}x=${position.x}`, `p${slot}y=${position.y}`);
  }
  lines.push("", "[ABILITIES]", `mode=${map.fixedAbilityIds?.length ? "fixed" : "global"}`, `ids=${map.fixedAbilityIds?.join(",") ?? ""}`);
  map.objects.forEach((object, index) => {
    lines.push("", `[${index + 1}]`, `type=${object.type}`, `x=${object.x}`, `y=${object.y}`);
    if (object.type === 5) lines.push(`telex=${object.targetX ?? 0}`, `teley=${object.targetY ?? 0}`);
  });
  return `${lines.join("\r\n")}\r\n`;
};

const runtimeKind: Readonly<Record<MdmObjectType, WorldObjectKind>> = {
  1: "wall",
  2: "wallLeft",
  3: "wallRight",
  4: "wallBottom",
  5: "teleport",
  6: "kicker",
  7: "horizontalKickerLeft",
  8: "horizontalKickerRight",
  9: "field",
  10: "field",
  11: "solidRopeTop",
  12: "solidRope"
};

const runtimeObjectName: Readonly<Record<MdmObjectType, string>> = {
  1: "obj_wall",
  2: "obj_wall_left",
  3: "obj_wall_right",
  4: "obj_wall_bottom",
  5: "obj_teleport",
  6: "obj_kicker",
  7: "obj_hkickerleft",
  8: "obj_hkickerright",
  9: "obj_field",
  10: "obj_field",
  11: "obj_solidropetop",
  12: "obj_solidrope"
};

const runtimePosition = (object: MdmObject): { x: number; y: number } => {
  switch (object.type) {
    case 5: return { x: object.x - 1, y: object.y + 1 };
    case 6: return { x: object.x - 1, y: object.y + 31 };
    case 7: return { x: object.x - 2, y: object.y + 1 };
    case 8: return { x: object.x + 22, y: object.y + 1 };
    case 9:
    case 10: return { x: object.x + 15, y: object.y + 64 };
    default: return { x: object.x, y: object.y };
  }
};

export const mdmToArenaMap = (map: MdmMap, id = "custom"): ArenaMap => {
  const slots = Object.keys(map.players).map(Number).sort();
  const playerCount = Math.max(2, Math.min(4, slots.length)) as PlayerCount;
  const objects: WorldObjectDefinition[] = [];
  map.objects.forEach((object, index) => {
    const position = runtimePosition(object);
    objects.push({
      id: `${id}:${index}`,
      kind: runtimeKind[object.type],
      sourceObject: runtimeObjectName[object.type],
      sprite: null,
      ...position,
      ...(object.type === 5 ? { targetX: object.targetX ?? 0, targetY: (object.targetY ?? 0) + 16 } : {})
    });
    if (object.type === 10) {
      objects.push({
        id: `${id}:${index}:kicker`,
        kind: "kicker",
        sourceObject: "obj_kicker",
        sprite: null,
        x: object.x - 1,
        y: object.y + 63
      });
    }
  });
  return {
    id,
    name: map.name,
    sourceRoom: null,
    width: 800,
    height: 600,
    playerCount,
    spawns: map.players as Record<PlayerSlot, { x: number; y: number }>,
    objects,
    fixedAbilityIds: map.fixedAbilityIds
  };
};
